import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { Target, Crosshair, Fish } from 'lucide-react';
import { useContactData } from '@/hooks/useContactData';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useSiteData } from '@/hooks/useSiteData';

export function Activities() {
  const { siteData } = useSiteData();
  const { contactInfo } = useContactData();
  const { navigationItems } = useNavigationData();

  const activities = [
    {
      title: "The Gun",
      icon: Target,
      description: "Pistol and rifle shooting with both indoor and outdoor ranges. Modern facilities support various calibers and shooting disciplines.",
      features: ["Indoor range", "Outdoor range", "Multiple shooting positions", "Various caliber support"]
    },
    {
      title: "The Bow", 
      icon: Crosshair,
      description: "Archery facilities for traditional and compound bows. Practice your precision with our dedicated archery range.",
      features: ["Dedicated archery range", "Traditional & compound bows", "Target practice", "Beginner friendly"]
    },
    {
      title: "The Rod",
      icon: Fish,
      description: "Fishing activities and education. Learn about local fishing opportunities and conservation efforts.",
      features: ["Fishing education", "Conservation programs", "Local fishing spots", "Technique workshops"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Activities - {siteData.title}</title>
        <meta name="description" content="Explore shooting, archery, and fishing activities at Wayland Rod and Gun Club. From beginner to advanced, we offer programs for all skill levels." />
        <meta name="keywords" content="shooting, archery, fishing, gun range, activities, outdoor sports, Wayland Rod Gun Club" />
      </Helmet>

      <Layout contactInfo={contactInfo} navigationItems={navigationItems}>
        <div className="p-4 sm:p-8 space-y-8 sm:space-y-12 max-w-6xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-cursive text-primary-700 mb-4">
              Club Activities
            </h1>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto leading-relaxed">
              From precision shooting to traditional archery and fishing education, 
              we offer diverse outdoor activities for all skill levels.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {activities.map((activity, index) => {
              const IconComponent = activity.icon;
              return (
                <div key={index} className="card-professional hover:shadow-elegant transition-shadow">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                      <IconComponent className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-xl font-display font-semibold text-secondary">
                      {activity.title}
                    </h2>
                  </div>
                  
                  <p className="text-foreground/80 mb-6 leading-relaxed">
                    {activity.description}
                  </p>
                  
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">
                      Features
                    </h3>
                    <ul className="space-y-1">
                      {activity.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-foreground/70">
                          <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="card-professional bg-muted/20">
            <div className="text-center">
              <h2 className="text-2xl font-display font-semibold text-secondary mb-4">
                Safety First
              </h2>
              <p className="text-foreground/80 max-w-3xl mx-auto leading-relaxed">
                All activities are conducted under strict safety protocols. We offer Massachusetts State Police 
                certified Basic Firearms Safety courses and emphasize proper technique and safety in all disciplines.
              </p>
              <div className="mt-6">
                <a 
                  href="/rules" 
                  className="inline-flex items-center px-6 py-3 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-lg font-semibold transition-colors"
                >
                  View Safety Rules
                </a>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}